import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import styles from "./LessonPreparations.module.scss";
import RightIcon from "@assets/icons/chewron_right.svg?react";
import PlusIcon from "@assets/icons/plus_circle.svg?react";
import CloseIcon from "@assets/icons/close.svg?react";
import IconPDF from "@assets/icons/content_type_pdf.svg?react";
import IconAudio from "@assets/icons/content_type_audio.svg?react";
import IconSlide from "@assets/icons/content_type_slide.svg?react";
import IconVideo from "@assets/icons/content_type_video.svg?react";
import IconForm from "@assets/icons/content_type_feedback.svg?react";
import SlideIcon from "@assets/icons/content_type_slide_com.svg?react";

import { SllModule, ContentType } from "@types";
import SectionContents from "../section-contents/LessonMaterialsContents";
import LocalPopup from "src/components/local-popup/LocalPopup";
import { useEditLesson } from "../../useEditLesson";
import {
  useSllCreateModuleMutation,
  LessonType,
  SllModuleFragmentDoc,
} from "@api/graphql-codegen";
import Spinner from "@components/spinner/Spinner";
import { logger } from "../../../../../utils/logger";

type Props = {
  title: string;
  contents?: SllModule[];
  lessonType: LessonType | undefined;
  lessonOpened: boolean;
};

function Preparations(props: Props) {
  const { title, contents = [], lessonType, lessonOpened } = props;
  const { lesson, setContent, setLesson, setSelectionLevel } = useEditLesson();

  const [createNewSllModule, { loading }] = useSllCreateModuleMutation({
    update(cache, res) {
      const data = res.data?.sllCreateModule;
      if (data && lesson) {
        if (lessonType === LessonType.PREPARATION) {
          const newSllModuleRef = cache.writeFragment({
            data,
            fragment: SllModuleFragmentDoc,
          });
          cache.modify({
            id: `SLLLesson:${lesson?.id}`,
            fields: {
              materials(existingRefs = []) {
                return [...existingRefs, newSllModuleRef];
              },
            },
          });
          setLesson({
            ...lesson,
            materials: [...(lesson?.materials || []), { ...data }],
          });
        }
        setContent(data);
        setSelectionLevel("Content");
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
      alert("Something went wrong.");
    },
  });

  const [opened, setOpened] = useState(true);
  const [createPopupOpened, setCreatePopupOpened] = useState(false);

  const createContent = useCallback(
    (contentType: ContentType) => () => {
      if (lesson?.id) {
        const nextPosition =
          contents.reduce((acc, c) => {
            if (c.position && c.position! > acc) return c.position;
            return acc;
          }, 0) + 1;
        createNewSllModule({
          variables: {
            input: {
              lessonId: lesson?.id,
              type: contentType,
              lessonType: lessonType,
              position: nextPosition,
            },
          },
        });
      }
      handleCloseCreatePopup();
    },
    [contents, createNewSllModule, lesson?.id, lessonType],
  );

  useEffect(() => {
    if (lessonOpened) setOpened(true);
  }, [lessonOpened]);

  const handleToggle = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const _opened = !opened;
    setOpened(_opened);
  };

  const wrapperClassName = useMemo(() => {
    const classes = [styles.wrapper];
    if (opened) {
      classes.push(styles.opened);
    }

    return classes.join(" ");
  }, [opened]);

  const handleShowCreatePopup = () => {
    setCreatePopupOpened(true);
  };

  const handleCloseCreatePopup = () => {
    setCreatePopupOpened(false);
  };

  const popupContent = useMemo(() => {
    return (
      <div className={styles.popup}>
        <div className={styles.popupHeader}>
          <div className={styles.popupTitle}>Add content</div>
          <div
            className={styles.popupCloseWrapper}
            onClick={handleCloseCreatePopup}
          >
            <CloseIcon />
          </div>
        </div>
        <div>
          <div className={styles.popupContent}>
            <div
              className={styles.popupItem}
              onClick={createContent(ContentType.VIDEO)}
            >
              <IconVideo />
              <div className={styles.itemName}>Video</div>
            </div>
            <div
              className={styles.popupItem}
              onClick={createContent(ContentType.AUDIO)}
            >
              <IconAudio />
              <div className={styles.itemName}>Audio</div>
            </div>
            <div
              className={styles.popupItem}
              onClick={createContent(ContentType.SLIDE)}
            >
              <IconSlide />
              <div className={styles.itemName}>Slides</div>
            </div>
          </div>
          <div className={styles.popupContent}>
            <div
              className={styles.popupItem}
              onClick={createContent(ContentType.PDF)}
            >
              <IconPDF />
              <div className={styles.itemName}>PDF</div>
            </div>
            <div
              className={styles.popupItemForm}
              onClick={createContent(ContentType.FORM)}
            >
              <IconForm />
              <div className={styles.itemName}>Form</div>
            </div>
            <div
              className={styles.popupItem}
              onClick={createContent(ContentType.PARENT)}
            >
              <div>PS</div>
              <div className={styles.itemName}>Parent</div>
            </div>
          </div>
          <div className={styles.popupContent}>
            <div
              className={styles.popupItem}
              onClick={createContent(ContentType.ZIPSLIDE)}
            >
              <SlideIcon />
              <div className={styles.itemName}>Zip Slides</div>
            </div>
          </div>
        </div>
      </div>
    );
  }, [createContent]);

  return (
    <div className={wrapperClassName}>
      <div onClick={handleToggle} className={styles.header}>
        <RightIcon className={styles.rightIcon} />
        <div className={styles.title}>{title}</div>
      </div>
      <div className={styles.content}>
        <SectionContents contents={contents} lessonType={lessonType} />
        <LocalPopup
          content={popupContent}
          open={createPopupOpened}
          onClose={handleCloseCreatePopup}
        >
          <div
            className={styles.addContentWrapper}
            onClick={handleShowCreatePopup}
          >
            <PlusIcon className={styles.addButtonIcon} />
            <div className={styles.addButtonTitle}>Add new module</div>
            {loading ? (
              <div className={styles.loading}>
                <Spinner width={20} height={20} />
              </div>
            ) : null}
          </div>
        </LocalPopup>
      </div>
    </div>
  );
}

export default React.memo(Preparations);
